import * as signalR from '@microsoft/signalr';
import { API_URL } from '@/http';
import { auth } from '@/auth';
import { IChatAIRAGResponse } from '@/models/chatai/IChatAIRAGResponse';
import { connectWithBackoff } from './connectWithBackoff';

/**
 * Singleton SignalR-клиент для ChatAI: получает push-уведомления `messageReceived`
 * от backend (RAG/Kafka pipeline через ChatAIHub.cs) и пробрасывает их подписчикам.
 *
 * Lifecycle: соединение поднимается, пока есть хотя бы один потребитель (ref-counting
 * через start()/stop()). При переподключении автоматически восстанавливает группу,
 * если ChatAIForm ждёт ответ на запрос.
 */

export type ChatAIMessageReceivedCallback = (requestId: string, payload: IChatAIRAGResponse) => void;
export type ChatAIConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
export type ChatAIConnectionStateCallback = (state: ChatAIConnectionState) => void;
export type ChatAIConversationsUpdatedCallback = () => void;

export type ChatAIHubMode = 'connecting' | 'push' | 'disabled';

class ChatAIHubClient {
    private connection: signalR.HubConnection;
    private messageCallbacks: ChatAIMessageReceivedCallback[] = [];
    private stateCallbacks: ChatAIConnectionStateCallback[] = [];
    private conversationsUpdatedCallbacks: ChatAIConversationsUpdatedCallback[] = [];
    private stopPromise: Promise<void> | null = null;
    private stopped = false;
    private refCount = 0;
    private startPromise: Promise<void> | null = null;
    private _mode: ChatAIHubMode = 'connecting';
    /** True с момента, когда start() был хоть раз вызван и не сброшен через stop(refCount=0). */
    private _started = false;
    /**
     * Поколение текущей сессии start(). Инкрементируется в stop(); используется in-flight IIFE
     * чтобы понять, что его сессия уже отменена (stop → start между start'ом и завершением
     * connectWithBackoff не должен породить второй параллельный IIFE на той же connection).
     */
    private generation = 0;
    /**
     * Текущая SignalR-группа (requestId), в которую мы вступили. После reconnect SignalR
     * забывает group membership — переподписку делаем через replay этого id, чтобы
     * потребители (ChatAIForm) не держали свою книгу и не подписывались на onreconnected.
     * На странице может быть открыт только один чат с одним pending request за раз —
     * поэтому хранение Set'а избыточно.
     */
    private currentGroup: string | null = null;

    constructor() {
        const logLevel = import.meta.env.PROD ? signalR.LogLevel.Warning : signalR.LogLevel.Trace;

        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(logLevel)
            .withUrl(`${API_URL}/hubs/chatAI`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .withAutomaticReconnect()
            .build();

        this.connection.on('messageReceived', (requestId: string, payload: IChatAIRAGResponse) => {
            this.messageCallbacks.forEach((cb) => cb(requestId, payload));
        });

        // Backend пушит без payload — клиент сам перезапрашивает список тредов.
        this.connection.on('conversationsUpdated', () => {
            this.conversationsUpdatedCallbacks.forEach((cb) => cb());
        });

        this.connection.onreconnecting(() => this.emitState('reconnecting'));
        this.connection.onreconnected(() => {
            this.emitState('connected');
            this.replayCurrentGroup();
        });
        this.connection.onclose(() => this.emitState('disconnected'));
    }

    public get mode(): ChatAIHubMode {
        return this._mode;
    }

    public get connectionState(): ChatAIConnectionState {
        // Bootstrap-фаза (возвращаем 'connecting'/'reconnecting', а не 'disconnected') в двух случаях:
        //   1) до первого start() и после stop() — нет активного потребителя (_started=false);
        //   2) идёт текущий start(): connectWithBackoff ещё не вернул результат (_mode='connecting'),
        //      между _started=true и emitState('connected') есть микро-окно, в которое не должны
        //      моргнуть 'disconnected' (повторный open чата ловил это при наличии stale lastUpdate).
        if (!this._started || this._mode === 'connecting') {
            return this.connection.state === signalR.HubConnectionState.Reconnecting
                ? 'reconnecting'
                : 'connecting';
        }
        switch (this.connection.state) {
            case signalR.HubConnectionState.Connected:
                return 'connected';
            case signalR.HubConnectionState.Reconnecting:
                return 'reconnecting';
            case signalR.HubConnectionState.Connecting:
                return 'connecting';
            default:
                return 'disconnected';
        }
    }

    public get isConnected(): boolean {
        return this.connection.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Ref-counted старт: первый вызывающий запускает соединение, остальные ждут готовности.
     * stop() декрементит счётчик; реальный stop() выполняется при достижении нуля.
     */
    public async start(): Promise<void> {
        this.refCount += 1;
        if (this.startPromise) {
            return this.startPromise;
        }
        const myGeneration = ++this.generation;
        this.stopped = false;
        this._started = true;
        this._mode = 'connecting';
        this.startPromise = (async () => {
            const result = await connectWithBackoff(this.connection, 'ChatAIHub', {
                isStopped: () => this.stopped || myGeneration !== this.generation,
                consumeStopPromise: () => {
                    const p = this.stopPromise;
                    this.stopPromise = null;
                    return p;
                },
                on404: () => {
                    if (myGeneration !== this.generation) return;
                    this._mode = 'disabled';
                    this.emitState('disconnected');
                },
            });
            // Если поколение поменялось — нашу сессию отменили через stop(); не эмитим событий
            // и не трогаем _mode, чтобы не перезатереть состояние новой сессии.
            if (myGeneration !== this.generation) return;
            if (result === 'connected') {
                this._mode = 'push';
                this.emitState('connected');
            } else if (result === 'failed') {
                this._mode = 'disabled';
                this.emitState('disconnected');
            }
        })();
        await this.startPromise;
    }

    public stop(): void {
        if (this.refCount > 0) {
            this.refCount -= 1;
        }
        if (this.refCount > 0) return;

        this.generation += 1;
        this.stopped = true;
        this.startPromise = null;
        this._started = false;
        this.currentGroup = null;
        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.stopPromise = this.connection.stop().catch(() => {});
        }
    }

    public async joinGroup(requestId: string): Promise<void> {
        this.currentGroup = requestId;
        if (!this.isConnected) return;
        await this.connection.invoke('JoinGroup', requestId);
    }

    public async leaveGroup(requestId: string): Promise<void> {
        if (this.currentGroup === requestId) this.currentGroup = null;
        if (!this.isConnected) return;
        await this.connection.invoke('LeaveGroup', requestId);
    }

    private replayCurrentGroup(): void {
        const requestId = this.currentGroup;
        if (!requestId) return;
        this.connection.invoke('JoinGroup', requestId).catch(() => {});
    }

    public onMessageReceived(callback: ChatAIMessageReceivedCallback): () => void {
        this.messageCallbacks.push(callback);
        return () => {
            this.messageCallbacks = this.messageCallbacks.filter((cb) => cb !== callback);
        };
    }

    public onConversationsUpdated(callback: ChatAIConversationsUpdatedCallback): () => void {
        this.conversationsUpdatedCallbacks.push(callback);
        return () => {
            this.conversationsUpdatedCallbacks = this.conversationsUpdatedCallbacks.filter((cb) => cb !== callback);
        };
    }

    public onStateChange(callback: ChatAIConnectionStateCallback): () => void {
        this.stateCallbacks.push(callback);
        return () => {
            this.stateCallbacks = this.stateCallbacks.filter((cb) => cb !== callback);
        };
    }

    private emitState(state: ChatAIConnectionState): void {
        this.stateCallbacks.forEach((cb) => cb(state));
    }
}

export const chatAIHub = new ChatAIHubClient();
